import { memo, MouseEvent } from "react";
import { Box, Link, SxProps, Theme } from "@mui/material";
import { JUMP_MARKS_BAR_TEST_ID } from "src/components-mui/common/jump-marks/jump-marks-bar.test-ids";
import { useJumpMarksNavigation, useJumpMarksSelector, useJumpMarksStickyTopPx } from "src/components-mui/common/jump-marks/jump-marks.context";
import { IJumpMarkItem } from "src/components-mui/common/jump-marks/jump-marks.types";
import { getJumpMarkHref, getNearestScrollLeft } from "src/components-mui/common/jump-marks/jump-marks.utils";
import { Container } from "src/components-mui/layout/container";
import { TResponsivePx } from "src/types/responsive";
import { getSmoothScrollBehavior } from "src/utils/dom/reduced-motion/reduced-motion";
import { toSxArray } from "src/utils/mui/sx/sx";

const JUMP_MARK_TAB_HEIGHT_PX = { xs: 40, sm: 56 } as const;
const JUMP_MARKS_BAR_PADDING_TOP_PX = 8;

export const JUMP_MARKS_BAR_HEIGHT_PX: TResponsivePx = {
    xs: JUMP_MARK_TAB_HEIGHT_PX.xs + JUMP_MARKS_BAR_PADDING_TOP_PX,
    sm: JUMP_MARK_TAB_HEIGHT_PX.sm + JUMP_MARKS_BAR_PADDING_TOP_PX,
};

const CONTAINER_PX_WITHOUT_MOBILE = [0, null, null, null];

const BAR_SX: SxProps<Theme> = theme => ({
    position: "sticky",
    zIndex: theme.customZIndex?.jumpMarksBar,
    pt: `${JUMP_MARKS_BAR_PADDING_TOP_PX}px`,
    backgroundColor: "common.white",
});

const TAB_LIST_SX: SxProps<Theme> = {
    display: "flex",
    height: JUMP_MARK_TAB_HEIGHT_PX,
    m: 0,
    p: 0,
    listStyle: "none",
    overflowX: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
        display: "none",
    },
    borderBottom: "1px solid",
    borderColor: "gray.200",
};

const TAB_LIST_ITEM_SX: SxProps<Theme> = {
    display: "flex",
    flexShrink: 0,
};

const getTabSx = (isActive: boolean): SxProps<Theme> => {
    const color = isActive ? "gray.800" : "gray.400";

    return {
        display: "flex",
        px: { xs: 2, md: 3 },
        color,
        fontSize: { xs: "sm", sm: "md" },
        fontWeight: "semibold",
        "&:hover": {
            color,
        },
        "@media (hover: hover)": {
            "&:hover": {
                color: "gray.800",
            },
        },
        "&:focus": {
            color,
        },
        "&.Mui-focusVisible": {
            boxShadow: theme => `inset ${theme.customShadows?.focusVisible}`,
        },
    };
};

const getTabLabelSx = (isActive: boolean): SxProps<Theme> => ({
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap",
    borderBottom: "3px solid",
    borderColor: isActive ? "gray.800" : "transparent",
});

const ACTIVE_TAB_SX = getTabSx(true);
const INACTIVE_TAB_SX = getTabSx(false);
const ACTIVE_TAB_LABEL_SX = getTabLabelSx(true);
const INACTIVE_TAB_LABEL_SX = getTabLabelSx(false);

/** Scrolls only the tab list: `tab.scrollIntoView()` would also scroll the page in Safari, which ignores `container: "nearest"`. */
const scrollTabIntoView = (tab: HTMLAnchorElement | null) => {
    const tabList = tab?.closest("ul");

    if (!tab || !tabList) {
        return;
    }

    const tabListLeft = tabList.getBoundingClientRect().left;
    const { left, right } = tab.getBoundingClientRect();
    const scrollLeft = getNearestScrollLeft({
        itemStart: left - tabListLeft + tabList.scrollLeft,
        itemEnd: right - tabListLeft + tabList.scrollLeft,
        scrollLeft: tabList.scrollLeft,
        viewportWidth: tabList.clientWidth,
    });

    if (scrollLeft !== tabList.scrollLeft) {
        tabList.scrollTo({ left: scrollLeft, behavior: getSmoothScrollBehavior() });
    }
};

export type TJumpMarkTabTrackingAttributes = { className?: string } & { [attribute: `data-${string}`]: string };

type TGetTabTrackingAttributes = (item: IJumpMarkItem) => TJumpMarkTabTrackingAttributes;

interface IJumpMarkTabProps {
    item: IJumpMarkItem;
    href: string;
    isActive: boolean;
    shouldScrollIntoView: boolean;
    onNavigate: (id: string) => void;
    getTrackingAttributes?: TGetTabTrackingAttributes;
}

const JumpMarkTab = memo(function JumpMarkTab({ item, href, isActive, shouldScrollIntoView, onNavigate, getTrackingAttributes }: IJumpMarkTabProps) {
    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        onNavigate(item.id);
    };

    return (
        <Box component="li" sx={TAB_LIST_ITEM_SX}>
            <Link
                ref={shouldScrollIntoView ? scrollTabIntoView : undefined}
                href={href}
                variant="unstyled"
                underline="none"
                aria-current={isActive ? "location" : undefined}
                onClick={handleClick}
                {...getTrackingAttributes?.(item)}
                sx={isActive ? ACTIVE_TAB_SX : INACTIVE_TAB_SX}
            >
                <Box component="span" sx={isActive ? ACTIVE_TAB_LABEL_SX : INACTIVE_TAB_LABEL_SX}>
                    {item.label}
                </Box>
            </Link>
        </Box>
    );
});

interface IJumpMarkTabListProps {
    getTabTrackingAttributes?: TGetTabTrackingAttributes;
}

const JumpMarkTabList = ({ getTabTrackingAttributes }: IJumpMarkTabListProps) => {
    const { items, navigateToSection } = useJumpMarksNavigation();
    const activeId = useJumpMarksSelector(state => state.jumpTargetId ?? state.urlAnchorId ?? state.visibleId);
    const isJumping = useJumpMarksSelector(state => state.jumpTargetId !== null);

    return (
        // role="list": Safari drops the list semantics of a `list-style: none` list.
        <Box component="ul" role="list" sx={TAB_LIST_SX}>
            {items.map(item => (
                <JumpMarkTab
                    key={item.id}
                    item={item}
                    href={getJumpMarkHref({ items, id: item.id })}
                    isActive={item.id === activeId}
                    shouldScrollIntoView={item.id === activeId && !isJumping}
                    onNavigate={navigateToSection}
                    getTrackingAttributes={getTabTrackingAttributes}
                />
            ))}
        </Box>
    );
};

interface IJumpMarksBarProps {
    label: string;
    getTabTrackingAttributes?: TGetTabTrackingAttributes;
    sx?: SxProps<Theme>;
}

export const JumpMarksBar = ({ label, getTabTrackingAttributes, sx }: IJumpMarksBarProps) => {
    const stickyTopPx = useJumpMarksStickyTopPx();

    return (
        <Box component="nav" aria-label={label} data-test-id={JUMP_MARKS_BAR_TEST_ID} sx={[BAR_SX, { top: stickyTopPx }, ...toSxArray(sx)]}>
            <Container px={CONTAINER_PX_WITHOUT_MOBILE}>
                <JumpMarkTabList getTabTrackingAttributes={getTabTrackingAttributes} />
            </Container>
        </Box>
    );
};
